import { CopyableContent } from "@/src/components/copyable-content";
import { Card, CardContent, CardHeader, CardTitle } from "@/src/components/shadcn/ui/card";
import { cn } from "@/src/lib/shadcn-utils";

const HEADER_VARIANT_CLASS = {
  outline: "bg-muted/40",
  success: "border-success bg-success text-success-foreground",
  caution: "border-caution bg-caution text-caution-foreground",
  qa: "border-qa bg-qa text-qa-foreground",
  verified: "border-verified bg-verified text-verified-foreground",
  failed: "border-failed bg-failed text-failed-foreground",
  warn: "border-warn bg-warn text-warn-foreground",
  destructive: "border-destructive bg-destructive text-destructive-foreground",
} as const;

const CARD_VARIANT_CLASS = {
  outline: "",
  success: "border-success",
  caution: "border-caution",
  qa: "border-qa",
  verified: "border-verified",
  failed: "border-failed",
  warn: "border-warn",
  destructive: "border-destructive",
} as const;

const COL_SPAN_CLASS = {
  1: "",
  2: "col-span-2",
  3: "col-span-full",
} as const;

export type ValerieCardHeaderVariant = keyof typeof HEADER_VARIANT_CLASS;
export type ValerieCardColSpan = keyof typeof COL_SPAN_CLASS;

export type ValerieCardDetail = { label: string; value: string | null | undefined };

/**
 * Standard Valerie card: muted header band with an optional icon, a prominent title, an optional
 * row of label/value `details` (DOB, IDs, dates…), and an actions slot on the right. Pair the body with
 * `ValerieCardSection` + `ValerieCardField` (or `FieldLayout`) so every card shares the same
 * subheaders, 3-column grid, and label-over-value fields.
 *
 * Keep it dumb: callers own everything inside `children` and `actions`. Prefer this over raw
 * Card/CardHeader composition when a card has a titled header.
 */

export function ValerieCard({
  title,
  description,
  details,
  icon,
  actions,
  className,
  variant = "outline",
  contentClassName,
  children,
  ...props
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  details?: ValerieCardDetail[];
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  variant?: ValerieCardHeaderVariant;
  contentClassName?: string;
  children: React.ReactNode;
} & Omit<React.ComponentProps<typeof Card>, "title">) {
  const presentDetails = (details ?? []).filter((detail) => detail.value?.trim());
  const hasSubheader = !!description || presentDetails.length > 0;
  const subheaderColor = variant === "outline" ? "text-muted-foreground" : "text-current opacity-80";

  return (
    <Card className={cn("overflow-hidden", CARD_VARIANT_CLASS[variant], className)} {...props}>
      <CardHeader
        className={cn(
          "flex-row items-start justify-between gap-2 space-y-0 border-b px-4 py-2",
          hasSubheader && "flex-wrap",
          HEADER_VARIANT_CLASS[variant]
        )}
      >
        <div className={cn("min-w-0 flex-1", hasSubheader && "basis-48")}>
          <CardTitle className="flex min-h-7 min-w-0 items-center gap-2 font-sans text-base font-semibold leading-tight tracking-normal">
            {icon ? (
              <span
                className={cn(
                  "flex shrink-0 items-center [&>svg]:h-4 [&>svg]:w-4",
                  variant === "outline" ? "text-muted-foreground" : "text-current opacity-90"
                )}
              >
                {icon}
              </span>
            ) : null}
            <span className="block min-w-0 whitespace-normal break-words">{title}</span>
          </CardTitle>
          {description ? <div className={cn("text-sm", subheaderColor)}>{description}</div> : null}
          {presentDetails.length > 0 ? (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              {presentDetails.map((detail) => (
                <span key={detail.label}>
                  <span className={subheaderColor}>{detail.label}</span>{" "}
                  <CopyableContent text={detail.value?.trim() ?? ""} className="text-current" />
                </span>
              ))}
            </div>
          ) : null}
        </div>
        {actions ? <div className="flex min-h-7 shrink-0 items-center gap-2">{actions}</div> : null}
      </CardHeader>
      <CardContent className={cn("p-4", contentClassName)}>{children}</CardContent>
    </Card>
  );
}

export function ValerieCardSectionTitle({ children }: { children: React.ReactNode }) {
  return <h4 className="text-xs font-semibold uppercase tracking-wide text-foreground/70">{children}</h4>;
}

export function ValerieCardSection({
  title,
  children,
  className,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className="min-w-0 space-y-2">
      {title ? <ValerieCardSectionTitle>{title}</ValerieCardSectionTitle> : null}
      <div className={cn("grid grid-cols-3 gap-x-6 gap-y-3 text-sm", className)}>{children}</div>
    </section>
  );
}

export function ValerieCardSections({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("space-y-7", className)}>{children}</div>;
}

export function ValerieCardField({
  label,
  value,
  children,
  colSpan = 1,
  startsRow = false,
  className,
}: {
  label: React.ReactNode;
  value?: string | null;
  children?: React.ReactNode;
  colSpan?: ValerieCardColSpan;
  startsRow?: boolean;
  className?: string;
}) {
  const trimmedValue = value?.trim();

  if (!children && !trimmedValue) {
    return null;
  }

  return (
    <div
      className={cn(
        "min-w-0 space-y-0.5 whitespace-normal [overflow-wrap:anywhere]",
        COL_SPAN_CLASS[colSpan],
        startsRow && "col-start-1",
        className
      )}
    >
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="leading-5">{children ?? <CopyableContent text={trimmedValue ?? ""} />}</div>
    </div>
  );
}
